import { useSession } from "./useSession";
import type { Card, Permissions } from "../types/domain";

export interface CardPerms {
  /** Можно ли редактировать карточку (PATCH/move/duplicate). */
  canEdit: boolean;
  /** Можно ли удалить карточку. Совпадает с canEdit — у Card нет отдельной
   *  роли «исполнителя», так что владелец всё может. */
  canDelete: boolean;
}

/** Зеркало серверного `isCardOwnScope`: роль работает в режиме «только свои»,
 *  если есть cards.viewOwn, но нет полного cards.view. */
function isCardOwnScope(perms: Permissions | undefined): boolean {
  const c = perms?.cards;
  if (!c) return false;
  return c.viewOwn === true && c.view !== true;
}

/**
 * Зеркало серверного assertCanModifyCard для UI.
 *  - полный scope: матрица cards.edit/delete действует на любые карточки.
 *  - own scope: только если `createdById === me`. Legacy-карточки
 *    (createdById = null) трактуются как «не свои».
 */
export function useCardPermissions(card: Card | null | undefined): CardPerms {
  const session = useSession();
  const me = session.data;
  if (!me || !card) return { canEdit: false, canDelete: false };
  const c = me.permissions?.cards;
  if (!c) return { canEdit: false, canDelete: false };

  const canEditPerm = c.edit === true;
  const canDeletePerm = c.delete === true;
  if (!isCardOwnScope(me.permissions)) {
    return { canEdit: canEditPerm, canDelete: canDeletePerm };
  }

  const isOwner = card.createdById === me.id;
  return { canEdit: canEditPerm && isOwner, canDelete: canDeletePerm && isOwner };
}
